import type { Locale } from "#i18n";
import otaClient from "@crowdin/ota-client";
import { changeLocale as changeFormKitLocale } from "@formkit/i18n";

import { i18nRedirectCookieKey } from "~/constants/cookieKeys";
import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

export default function useSetLocale() {
  const { $segment, $i18n } = useNuxtApp();

  const router = useRouter();
  const switchLocalePath = useSwitchLocalePath();

  const setLocale = async (
    languageCode: Locale,
    options: { mode?: "local" | "plugin" } = { mode: "local" },
  ) => {
    const dateNow = new Date();
    const nuxtApp = useNuxtApp();
    const cookie = useCookie(i18nRedirectCookieKey, {
      sameSite: "none",
      secure: true,
      // Expires in a year
      expires: new Date(dateNow.setDate(dateNow.getDate() + 365)),
      path: "/",
    });

    // we want to keep the Accept-Language header in-sync with the user's
    // language choice so we always get responses in the correct language
    getUniversalAxios().defaults.headers.common["Accept-Language"] =
      languageCode;
    $segment.browser.value.identify({
      language: languageCode,
    });

    changeFormKitLocale(languageCode);
    cookie.value = languageCode;

    if (languageCode === $i18n.locale.value) {
      await updateCrowdinTranslations(languageCode);

      return;
    }

    const replaceRoute = () => router.replace(switchLocalePath(languageCode));

    if (options.mode === "local") {
      await replaceRoute();
      await updateCrowdinTranslations(languageCode);
    } else {
      await $i18n.setLocale(languageCode);
      // Redirect with context if we're coming from a plugin
      nuxtApp.runWithContext(replaceRoute);

      await updateCrowdinTranslations(languageCode);
    }
  };

  const updateCrowdinTranslations = async (localeCode: Locale) => {
    if (import.meta.client) {
      const config = useRuntimeConfig();
      const distributionHash = config.public.crowdinDistributionHash;

      if (!distributionHash) return;

      try {
        const client = new otaClient(distributionHash);

        const newTranslations = await client.getStringsByLocale(localeCode);

        if (newTranslations && Object.keys(newTranslations).length > 0) {
          $i18n.mergeLocaleMessage(localeCode, newTranslations);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("Failed to update Crowdin translations:", error);
        }
      }
    }
  };

  return {
    setLocale,
  };
}
